import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { getGenderIcon } from "@/hooks/use-gender";
import { toIsoDate } from "@/lib/therapist-scheduling/date";
import { therapistTypeLabel } from "@/lib/therapist-scheduling/employment-type";
import { filterBookableTherapists } from "@/lib/therapist-scheduling/slot-gating";
import type {
	AvailableTherapist,
	SlotSelection,
	TimeSlot,
} from "@/lib/therapist-scheduling/types";
import { useTherapistEnumLabel } from "@/lib/therapists/enum-label";
import { cn } from "@/lib/utils";
import { TherapistSlotList } from "./therapist-slot-list";

type TherapistAvailabilityListProps = {
	therapists: AvailableTherapist[];
	date: Date;
	selection?: SlotSelection | null;
	onSelect: (selection: SlotSelection) => void;
	isLoading?: boolean;
};

function initials(name: string): string {
	return name
		.split(" ")
		.slice(0, 2)
		.map((p) => p[0]?.toUpperCase() ?? "")
		.join("");
}

/** Driving estimate in minutes: HERE Maps first, formula fallback, else null. */
function travelMinutes(therapist: AvailableTherapist): number | null {
	const here = therapist.travel_time_here_maps?.duration_minutes;
	const manual = therapist.travel_time_manual_calculation?.duration_minutes;
	const value = here ?? manual;
	return typeof value === "number" ? Math.round(value) : null;
}

/**
 * Renders ALL bookable therapists (positive status + non-empty slots, §9), each
 * with an inline slot list. Single selection lifts
 * `{ therapistId, date, start_time, end_time }`.
 */
export function TherapistAvailabilityList({
	therapists,
	date,
	selection,
	onSelect,
	isLoading = false,
}: TherapistAvailabilityListProps) {
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.schedule_picker.availability",
	});
	const enumLabel = useTherapistEnumLabel();
	if (isLoading) {
		return (
			<div className="flex flex-col gap-3">
				<Skeleton className="h-28 w-full rounded-lg" />
				<Skeleton className="h-28 w-full rounded-lg" />
			</div>
		);
	}

	const list = filterBookableTherapists(therapists);
	const iso = toIsoDate(date);

	if (list.length === 0) {
		return (
			<div className="rounded-lg border border-border border-dashed p-6 text-center text-muted-foreground text-sm">
				{t("empty")}
			</div>
		);
	}

	const handleSlot = (therapist: AvailableTherapist, slot: TimeSlot) => {
		onSelect({
			therapistId: therapist.id,
			therapistName: therapist.full_name,
			therapistType: therapist.therapist_type,
			date: iso,
			start_time: slot.start_time,
			end_time: slot.end_time,
		});
	};

	return (
		<div className="flex flex-col gap-2">
			<p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
				{t("count", { count: list.length })}
			</p>
			<div className="flex max-h-[30rem] flex-col gap-2 overflow-y-auto pr-1">
				{list.map((therapist) => {
					const isSelectedTherapist = selection?.therapistId === therapist.id;
					const slotCount = therapist.available_slots?.length ?? 0;
					const specs = therapist.specializations;
					const travel = travelMinutes(therapist);
					return (
						<div
							key={therapist.id}
							className={cn(
								"flex flex-col gap-3 rounded-lg border border-border p-3 transition-colors",
								isSelectedTherapist && "border-primary ring-1 ring-primary",
							)}
						>
							<div className="flex items-start gap-3">
								<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent font-semibold text-accent-foreground text-sm">
									{initials(therapist.full_name)}
								</div>
								<div className="min-w-0 flex-1">
									<span className="font-semibold text-sm uppercase">
										{therapist.full_name}
									</span>
									{specs.length > 0 && (
										<p className="mt-0.5 truncate text-muted-foreground/75 text-xs">
											{specs
												.slice(0, 2)
												.map((spec) => enumLabel("specialization", spec))
												.join(" · ")}
											{specs.length > 2 && ` · +${specs.length - 2}`}
										</p>
									)}
								</div>
								<Badge
									variant={
										therapist.availability_status === "AVAILABLE"
											? "success"
											: "secondary"
									}
									className="shrink-0 py-0 px-1.5 rounded-full text-[10px]"
								>
									{therapist.availability_status === "AVAILABLE"
										? t("status_available")
										: t("status_partial")}
								</Badge>
							</div>
							<div className="flex flex-wrap items-center gap-1.5">
								{therapist.gender && (
									<span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 py-0.5 px-2 font-medium text-foreground text-xs uppercase">
										{getGenderIcon(therapist.gender, "size-3.5")}
										{therapist.gender}
									</span>
								)}
								<span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 py-0.5 px-2 font-medium text-foreground text-xs">
									<span className="font-normal text-muted-foreground">
										{t("distance_key")}
									</span>
									{travel != null
										? t("distance_with_travel", {
												km: therapist.distance_km.toFixed(1),
												min: travel,
											})
										: t("distance", { km: therapist.distance_km.toFixed(1) })}
								</span>
								<span className="inline-flex items-center gap-1 rounded-full border border-transparent bg-accent py-0.5 px-2 font-medium text-accent-foreground text-xs">
									{therapistTypeLabel(therapist.therapist_type)}
								</span>
							</div>
							<div className="flex flex-col gap-1">
								<span className="flex items-center gap-1.5 font-medium text-muted-foreground text-xs">
									<Clock className="size-3.5 shrink-0" />
									{t("slot_label", { count: slotCount })}
								</span>
								<TherapistSlotList
									therapist={therapist}
									date={date}
									selectedStartTime={
										isSelectedTherapist ? selection?.start_time : null
									}
									onSelectSlot={(slot) => handleSlot(therapist, slot)}
								/>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
